// ---------------------------------------------------------------------------
// DSS-7: Font picker — 20 curated Google Fonts + search + preview + recents
// ---------------------------------------------------------------------------
import { useState, useCallback, useId, useRef, useEffect } from "react";
import { Type, Search, Clock } from "lucide-react";

// ── Types ──
interface FontPickerProps {
  readonly value: string;
  readonly onChange: (fontFamily: string) => void;
}

// ── Constants ──
const CURATED_FONTS = [
  "Inter",
  "Roboto",
  "Open Sans",
  "Lato",
  "Montserrat",
  "Poppins",
  "Raleway",
  "Nunito",
  "Playfair Display",
  "Merriweather",
  "Source Sans 3",
  "Work Sans",
  "DM Sans",
  "Manrope",
  "Space Grotesk",
  "Outfit",
  "Plus Jakarta Sans",
  "Crimson Text",
  "Libre Baskerville",
  "Bitter",
] as const;

const RECENTS_KEY = "dss-font-picker-recents";
const MAX_RECENTS = 5;

// ── Font loading ──
const loadedFonts = new Set<string>();

function sanitizeFontName(fontName: string): string | null {
  const trimmed = fontName.trim();
  return /^[a-zA-Z0-9\s-]+$/.test(trimmed) ? trimmed : null;
}

export function buildGoogleFontHref(fontName: string): string | null {
  const safeName = sanitizeFontName(fontName);
  if (!safeName) return null;
  const family = safeName.replace(/\s+/g, "%20");
  return `https://fonts.googleapis.com/css2?family=${family}:wght@400;500;600;700&display=swap`;
}

export function ensureFontLoaded(fontName: string): void {
  const href = buildGoogleFontHref(fontName);
  if (!href) return;
  if (loadedFonts.has(fontName)) return;
  loadedFonts.add(fontName);

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.setAttribute("data-dss-font-href", fontName);
  document.head.appendChild(link);
}

// ── Recents persistence ──
function getRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === "string").slice(0, MAX_RECENTS);
  } catch {
    return [];
  }
}

function addRecent(fontName: string): void {
  const recents = getRecents().filter((f) => f !== fontName);
  recents.unshift(fontName);
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(recents.slice(0, MAX_RECENTS)));
  } catch {
    // Storage full — ignore
  }
}

// ── Component ──
export default function FontPicker({ value, onChange }: FontPickerProps) {
  const uid = useId();
  const searchId = `${uid}-search`;
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [recents, setRecents] = useState<string[]>(getRecents);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load the current font
  useEffect(() => {
    if (value) ensureFontLoaded(value);
  }, [value]);

  // Load all curated fonts for preview when dropdown opens
  useEffect(() => {
    if (!isOpen) return;
    for (const font of CURATED_FONTS) {
      ensureFontLoaded(font);
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const handleSelect = useCallback(
    (fontName: string) => {
      ensureFontLoaded(fontName);
      addRecent(fontName);
      setRecents(getRecents());
      onChange(fontName);
      setIsOpen(false);
      setQuery("");
    },
    [onChange],
  );

  const filtered = query
    ? CURATED_FONTS.filter((f) => f.toLowerCase().includes(query.toLowerCase()))
    : CURATED_FONTS;

  const recentFonts = recents.filter((f) => !query || f.toLowerCase().includes(query.toLowerCase()));

  return (
    <div ref={containerRef} className="relative flex flex-col gap-2">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <Type className="h-3.5 w-3.5" />
        Font Family
      </div>

      {/* Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-8 items-center justify-between rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-3 text-left text-[11px] text-[var(--color-foreground)] hover:border-[var(--color-primary)]/50"
        style={{ fontFamily: value || "inherit" }}
      >
        <span className="truncate">{value || "Select font..."}</span>
        <span className="ml-2 text-[10px] text-[var(--color-muted-foreground)]">
          {isOpen ? "\u25B2" : "\u25BC"}
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full z-50 mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] shadow-lg">
          {/* Search */}
          <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-3 py-2">
            <Search className="h-3.5 w-3.5 text-[var(--color-muted-foreground)]" />
            <input
              id={searchId}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search fonts..."
              autoFocus
              className="h-6 flex-1 bg-transparent text-[11px] text-[var(--color-foreground)] outline-none placeholder:text-[var(--color-muted-foreground)]"
            />
          </div>

          <div className="max-h-60 overflow-y-auto">
            {/* Recently used */}
            {recentFonts.length > 0 && !query && (
              <>
                <div className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium text-[var(--color-muted-foreground)]">
                  <Clock className="h-3 w-3" />
                  Recently Used
                </div>
                {recentFonts.map((font) => (
                  <FontOption
                    key={`recent-${font}`}
                    font={font}
                    isActive={value === font}
                    onClick={() => handleSelect(font)}
                  />
                ))}
                <div className="mx-3 border-b border-[var(--color-border)]" />
              </>
            )}

            {/* All fonts */}
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-[11px] text-[var(--color-muted-foreground)]">
                No fonts match &ldquo;{query}&rdquo;
              </div>
            ) : (
              filtered.map((font) => (
                <FontOption
                  key={font}
                  font={font}
                  isActive={value === font}
                  onClick={() => handleSelect(font)}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Font option row ──
interface FontOptionProps {
  readonly font: string;
  readonly isActive: boolean;
  readonly onClick: () => void;
}

function FontOption({ font, isActive, onClick }: FontOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex w-full items-center px-3 py-2 text-left text-[12px]",
        isActive
          ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
          : "text-[var(--color-foreground)] hover:bg-[var(--color-muted)]",
      ].join(" ")}
      style={{ fontFamily: font }}
    >
      {font}
    </button>
  );
}
